import { call, put, race, takeLatest, select } from 'redux-saga/effects';

import { createStandaloneToast, theme } from '@frontend/components';

import { viewer } from 'modules/common/viewer/data/actions';
import {
  getActivePartFileUuid,
  getPrintPageIdByNumber,
  getPrintPageScales,
} from 'modules/common/viewer/data/reducer';
import {
  partLibraryViewerPage,
  uploadSupportingFiles,
} from 'modules/viewerPage/data/actions';
import { PART_PANEL_STATES } from 'modules/viewerPage/data/constants';

import partService from 'services/partService';

import { applyRedactionsModal, types, updatePageScale } from './actions';
import { applyRedactions } from './actions';
import { ANNOTATION_CHANGE_EVENT } from './constants';
import { getCurrentPageNumber } from './reducer';

const { toast } = createStandaloneToast({ theme });

export default function* root() {
  yield takeLatest(types.APPLY_REDACTIONS_REQUEST, initiateRedaction);
  yield takeLatest(types.UPDATE_PAGE_SCALE_REQUEST, watchUpdatePageScale);
}

function* initiateRedaction(action) {
  const { fileName } = action.payload;

  yield race({
    applyRedactions: takeLatest(
      types.REDACT_FILE,
      watchApplyRedactions,
      fileName,
    ),
    close: takeLatest(types.CLOSE_APPLY_REDACTIONS_MODAL),
  });
}

function* watchApplyRedactions(fileName, action) {
  try {
    const { redactedBlob } = action.payload;
    const redactedFile = new File([redactedBlob], fileName, {
      type: 'application/pdf',
    });

    // Get the active part file UUID to use as redacted_from
    const activePartFileUuid = yield select(getActivePartFileUuid);

    yield put(
      uploadSupportingFiles.request({
        file: [redactedFile],
        redactedFrom: activePartFileUuid,
      }),
    );
    yield put(applyRedactionsModal.close());
    yield put(
      partLibraryViewerPage.setPartPanelState({
        partPanelState: PART_PANEL_STATES.FILES,
      }),
    );

    yield put(applyRedactions.success());
  } catch (error) {
    yield put(applyRedactions.error(error));
  }
}

function* watchUpdatePageScale(action) {
  try {
    const { annotationChangeEvent, pageNumber, scale } = action.payload;
    const activePartFileUuid = yield select(getActivePartFileUuid);
    const printPageId = yield select(
      getPrintPageIdByNumber,
      activePartFileUuid,
      pageNumber,
    );

    if (!scale.scaleFromDimension || !scale.scaleToDimension) {
      const message = 'Scale must be a valid number';
      yield put(updatePageScale.error(message));
      yield call(toast, {
        title: "Can't update page scale",
        description: message,
        status: 'error',
      });
      return;
    }

    const currentPageNumber = yield select(getCurrentPageNumber);
    const pageScales = yield select(getPrintPageScales);
    if (
      annotationChangeEvent === ANNOTATION_CHANGE_EVENT.ADD &&
      pageNumber !== currentPageNumber &&
      pageScales[pageNumber]
    ) {
      yield put(
        updatePageScale.error(
          'Adding a new measurement annotation on a page that already has a scale should not update the page scale',
        ),
      );
      return;
    } else {
      const printPage = yield call(
        partService.printPages.update,
        printPageId,
        scale,
      );
      yield put(viewer.receivePrintPage({ printPage }));
    }
    yield put(updatePageScale.success());
  } catch (error) {
    yield put(updatePageScale.error(error));
  }
}
